import { z } from "zod";

// Shared between apps/frontend (client-side validation + fetch call) and
// apps/backend (server-side validation) so the two never drift out of sync.
export const contactRequestSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120),
  workEmail: z.string().trim().email("Enter a valid email address").max(254),
  organization: z.string().trim().min(1, "Organization is required").max(160),
  role: z.string().trim().min(1, "Role or team is required").max(160),
  interest: z.string().trim().min(1, "Choose a focus area").max(160),
  message: z
    .string()
    .trim()
    .min(10, "Add a bit more detail (at least 10 characters)")
    .max(4000),
  // Hidden field real visitors never fill in; bots that auto-fill every
  // input trip it. Kept loose here (no maxLength) so the schema itself
  // never rejects a submission on this field — the route decides what to
  // do with a non-empty value instead of surfacing a validation error.
  company: z.string().optional().default(""),
});

export type ContactRequest = z.infer<typeof contactRequestSchema>;

// Keyed off contactRequestSchema's own fields, so a field can never be
// renamed here without this failing to compile.
export const contactFieldErrorsSchema = z.partialRecord(
  contactRequestSchema.keyof(),
  z.string(),
);
export type ContactFieldErrors = z.infer<typeof contactFieldErrorsSchema>;

// Machine-readable reason, always paired with a human-readable `error`
// string that's safe to show as-is. Callers should switch on `code`, not
// pattern-match `error` text — the string is free to change independently.
export const contactErrorCodeSchema = z.enum([
  "validation_error",
  "rate_limited",
  "send_failed",
  "network_error",
]);
export type ContactErrorCode = z.infer<typeof contactErrorCodeSchema>;

// Response contract validated at runtime on both ends, same as the
// request: the server checks its own output shape before sending, and the
// client checks what actually arrives before trusting it — neither side
// has to take "the other side matches the TS type" on faith.
export const contactResponseSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true) }),
  z.discriminatedUnion("code", [
    z.object({
      ok: z.literal(false),
      code: z.literal("validation_error"),
      error: z.string(),
      fieldErrors: contactFieldErrorsSchema,
    }),
    z.object({
      ok: z.literal(false),
      code: z.enum(["rate_limited", "send_failed", "network_error"]),
      error: z.string(),
    }),
  ]),
]);
export type ContactResponse = z.infer<typeof contactResponseSchema>;
